/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form } from 'react-final-form';
import { useSnackbar } from 'notistack';
import SelectField from 'common/forms/SelectField';
import {
  listFulfillingPharmaciesApi,
  listPharmaciesApi,
  transferOrdersToExternalPharmacyApi,
} from 'api/pharmacy';
import { transferOrderDetailsApi } from 'api/order';
import { INELIGIBLE_TRANSFER_OUT_STATUSES } from 'common/constants/orderStatusGroups';
import gender from 'common/constants/gender';

let cachedPharmacies = [];
let pharmaciesLoading = false;
let pharmaciesLoadAttempted = false;

let cachedFulfillingPharmacies = [];
let fulfillingPharmaciesLoading = false;
let fulfillingPharmaciesLoadAttempted = false;

const TransferOutDialog = ({ open, onClose, selectedLineItems, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pharmacies, setPharmacies] = useState(cachedPharmacies);
  const [isLoadingPharmacies, setIsLoadingPharmacies] = useState(false);
  const [fulfillingPharmacies, setFulfillingPharmacies] = useState(cachedFulfillingPharmacies);
  const [isLoadingFulfillingPharmacies, setIsLoadingFulfillingPharmacies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderDetails, setOrderDetails] = useState({});
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const formRef = useRef(null);

  const orderId = useMemo(() => {
    return selectedLineItems[0]?.orderId;
  }, [selectedLineItems]);

  const initialFulfillingPharmacyId = useMemo(() => {
    const orderFulfillingPharmacyId = orderDetails[orderId]?.fulfillingPharmacyId;
    if (!orderFulfillingPharmacyId || fulfillingPharmacies.length === 0) {
      return undefined;
    }
    const matchingPharmacy = fulfillingPharmacies.find(
      (pharmacy) => pharmacy.fulfillingPharmacyId === orderFulfillingPharmacyId
    );
    return matchingPharmacy ? matchingPharmacy.fulfillingPharmacyId : undefined;
  }, [orderDetails, orderId, fulfillingPharmacies]);

  useEffect(() => {
    if (initialFulfillingPharmacyId && formRef.current) {
      const currentValue = formRef.current.getFieldState('fulfillingPharmacyId')?.value;
      if (!currentValue) {
        formRef.current.change('fulfillingPharmacyId', initialFulfillingPharmacyId);
      }
    }
  }, [initialFulfillingPharmacyId]);

  useEffect(() => {
    if (open && orderId && !isLoadingOrderDetails && orderDetails[orderId] === undefined) {
      setIsLoadingOrderDetails(true);
      transferOrderDetailsApi({ orderId })
        .then((order) => {
          if (order) {
            const lineItemsWithRx = (order.lineItems || [])
              .filter((item) => item.rxNumber)
              .map((item) => ({
                rxNumber: item.rxNumber,
                itemName: item.itemName || '',
                overriddenItemName: item.overriddenItemName || '',
                quantity: item.quantity || null,
                refillsRemaining: item.refillsRemaining || null,
                writtenDate: item.writtenDate || null,
                directions: item.directions || null,
                prescriberName: item.prescriberName || '',
              }));

            const orderStatusIneligible = INELIGIBLE_TRANSFER_OUT_STATUSES.includes(order.status);

            const ineligibleLineItems = (order.lineItems || []).filter((item) => {
              const status = item.lineItemStatus || order.status;
              return INELIGIBLE_TRANSFER_OUT_STATUSES.includes(status);
            });

            const hasIneligibleItems = orderStatusIneligible || ineligibleLineItems.length > 0;

            let ineligibleItem = null;
            let ineligibleStatus = null;

            if (orderStatusIneligible) {
              ineligibleStatus = order.status;
              ineligibleItem = {
                rxNumber: (order.lineItems || []).find((item) => item.rxNumber)?.rxNumber || 'N/A',
                lineItemStatus: order.status,
              };
            } else if (ineligibleLineItems.length > 0) {
              ineligibleItem =
                ineligibleLineItems.find((item) => item.rxNumber) || ineligibleLineItems[0];
              ineligibleStatus = ineligibleItem?.lineItemStatus || order.status;
            }

            setOrderDetails((prev) => ({
              ...prev,
              [orderId]: {
                lineItemIds: (order.lineItems || []).map((item) => item.lineItemId),
                lineItemsWithRx,
                isEligible: !hasIneligibleItems,
                ineligibleItem: ineligibleItem
                  ? {
                      rxNumber: ineligibleItem.rxNumber || 'N/A',
                      status: ineligibleStatus,
                    }
                  : null,
                fulfillingPharmacyId: order.fulfillingPharmacyId,
                patientName: order.patientName,
                patientGender: order.patientGender,
                patientDateOfBirth: order.patientDateOfBirth,
              },
            }));
          } else {
            setOrderDetails((prev) => ({
              ...prev,
              [orderId]: null,
            }));
          }
          setIsLoadingOrderDetails(false);
        })
        .catch(() => {
          enqueueSnackbar('Failed to load order details', { variant: 'error' });
          setOrderDetails((prev) => ({
            ...prev,
            [orderId]: null,
          }));
          setIsLoadingOrderDetails(false);
        });
    }
  }, [open, orderId, isLoadingOrderDetails, orderDetails, enqueueSnackbar]);

  useEffect(() => {
    // Load external pharmacies
    if (cachedPharmacies.length > 0) {
      setPharmacies(cachedPharmacies);
    } else if (open && !pharmaciesLoadAttempted && !pharmaciesLoading) {
      pharmaciesLoading = true;
      pharmaciesLoadAttempted = true;
      setIsLoadingPharmacies(true);

      listPharmaciesApi({
        filter: { isExternal: true },
      })
        .then((response) => {
          const results = response.results || [];
          cachedPharmacies = results;
          setPharmacies(results);
          setIsLoadingPharmacies(false);
          pharmaciesLoading = false;
        })
        .catch(() => {
          enqueueSnackbar('Failed to load external pharmacies', { variant: 'error' });
          setIsLoadingPharmacies(false);
          pharmaciesLoading = false;
        });
    }

    // Load fulfilling pharmacies
    if (cachedFulfillingPharmacies.length > 0) {
      setFulfillingPharmacies(cachedFulfillingPharmacies);
    } else if (open && !fulfillingPharmaciesLoadAttempted && !fulfillingPharmaciesLoading) {
      fulfillingPharmaciesLoading = true;
      fulfillingPharmaciesLoadAttempted = true;
      setIsLoadingFulfillingPharmacies(true);

      listFulfillingPharmaciesApi({
        filter: { isUserAssociated: true },
      })
        .then((response) => {
          const results = response.results || [];
          cachedFulfillingPharmacies = results;
          setFulfillingPharmacies(results);
          setIsLoadingFulfillingPharmacies(false);
          fulfillingPharmaciesLoading = false;
        })
        .catch(() => {
          enqueueSnackbar('Failed to load fulfilling pharmacies', { variant: 'error' });
          setIsLoadingFulfillingPharmacies(false);
          fulfillingPharmaciesLoading = false;
        });
    }
  }, [open, enqueueSnackbar]);

  const handleClose = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setOrderDetails({});
      onClose();
    }
  };

  const handleSubmit = ({ fulfillingPharmacyId, transferPharmacyId }) => {
    if (!fulfillingPharmacyId || !transferPharmacyId) {
      enqueueSnackbar('Please select both Facility and External Pharmacy', {
        variant: 'warning',
      });
      return;
    }

    setIsSubmitting(true);

    transferOrdersToExternalPharmacyApi({
      orderIds: [orderId],
      transferPharmacyId,
      transferMethod: 'FAX',
      internalFulfillingPharmacyId: fulfillingPharmacyId,
    })
      .then((response) => {
        const { results } = response;
        const successCount = results.filter((r) => r.success).length;
        const failureCount = results.filter((r) => !r.success).length;

        if (failureCount === 0) {
          enqueueSnackbar(`Successfully transferred ${successCount} order(s)`, {
            variant: 'success',
          });
          onSuccess();
        } else {
          const failedOrders = results
            .filter((r) => !r.success)
            .map((r) => r.orderId)
            .join(', ');
          enqueueSnackbar(
            `Transferred ${successCount} order(s) successfully. Failed: ${failedOrders}`,
            { variant: 'warning' }
          );
          if (successCount > 0) {
            onSuccess();
          }
        }
        setIsSubmitting(false);
      })
      .catch(() => {
        enqueueSnackbar('Failed to transfer orders', { variant: 'error' });
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby='TransferOut-dialog-header'
      fullWidth
      maxWidth='sm'
      disableEscapeKeyDown
    >
      <DialogTitle id='TransferOut-dialog-header'>Transfer Out </DialogTitle>

      <Form
        onSubmit={handleSubmit}
        render={({ handleSubmit: formHandleSubmit, submitting, values, form }) => {
          formRef.current = form;

          return (
            <form noValidate onSubmit={formHandleSubmit}>
              <DialogContent>
                <Box sx={{ mb: 2 }}>
                  {isLoadingOrderDetails ? (
                    <Alert severity='info'>Loading order details...</Alert>
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Patient Information
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          <Typography variant='body2' color='textSecondary' sx={{ mb: 0.5 }}>
                            <b>Patient Name:</b>{' '}
                            {orderDetails[orderId]?.patientName ||
                              selectedLineItems[0]?.patientName ||
                              'N/A'}
                          </Typography>
                          {orderDetails[orderId]?.patientGender && (
                            <Typography variant='body2' color='textSecondary' sx={{ mb: 0.5 }}>
                              <b>Patient Gender:</b> {gender[orderDetails[orderId].patientGender]}
                            </Typography>
                          )}
                          {orderDetails[orderId]?.patientDateOfBirth && (
                            <Typography variant='body2' color='textSecondary'>
                              <b>Patient DOB:</b>{' '}
                              {new Date(
                                orderDetails[orderId].patientDateOfBirth
                              ).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {orderId && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant='body1' sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            Order ID: {orderId}
                          </Typography>
                          {(() => {
                            const details = orderDetails[orderId];
                            if (details === null) {
                              return (
                                <Typography variant='body2' color='textSecondary'>
                                  This order will be transferred out including all the items for
                                  this order.
                                </Typography>
                              );
                            }

                            // Check if order is eligible
                            if (details?.isEligible === false && details?.ineligibleItem) {
                              const { rxNumber, status } = details.ineligibleItem;
                              const statusDisplay = status;
                              return (
                                <Alert severity='warning' sx={{ mt: 1 }}>
                                  <Typography variant='body2'>
                                    This order is not eligible to transfer out as one of the line
                                    items (RX Number: <b>{rxNumber}</b>) is in{' '}
                                    <b>{statusDisplay}</b> state.
                                  </Typography>
                                </Alert>
                              );
                            }

                            const lineItemsWithRx = details?.lineItemsWithRx || [];
                            if (lineItemsWithRx.length > 0) {
                              return (
                                <>
                                  <Typography
                                    variant='subtitle2'
                                    sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}
                                  >
                                    Item Details:
                                  </Typography>
                                  <List dense sx={{ pl: 2, mb: 1 }}>
                                    {lineItemsWithRx.map((item) => (
                                      <ListItem key={item.rxNumber} disableGutters sx={{ py: 0.5 }}>
                                        <ListItemText
                                          primary={
                                            <Box>
                                              <Typography
                                                variant='body2'
                                                component='span'
                                                sx={{ display: 'block', mb: 0.5 }}
                                              >
                                                <b>RX Number:</b> {item.rxNumber}
                                              </Typography>
                                              <Typography
                                                variant='body2'
                                                component='span'
                                                sx={{ display: 'block', mb: 0.5 }}
                                              >
                                                <b>Item Name:</b>{' '}
                                                {item.overriddenItemName || item.itemName || 'N/A'}
                                              </Typography>
                                              {item.quantity && (
                                                <Typography
                                                  variant='body2'
                                                  component='span'
                                                  sx={{ display: 'block', mb: 0.5 }}
                                                >
                                                  <b>Quantity:</b> {item.quantity}
                                                </Typography>
                                              )}
                                              {item.refillsRemaining !== null &&
                                                item.refillsRemaining !== undefined && (
                                                  <Typography
                                                    variant='body2'
                                                    component='span'
                                                    sx={{ display: 'block', mb: 0.5 }}
                                                  >
                                                    <b>Refill Count:</b> {item.refillsRemaining}
                                                  </Typography>
                                                )}
                                              {item.writtenDate && (
                                                <Typography
                                                  variant='body2'
                                                  component='span'
                                                  sx={{ display: 'block', mb: 0.5 }}
                                                >
                                                  <b>Written Date:</b>{' '}
                                                  {new Date(item.writtenDate).toLocaleDateString()}
                                                </Typography>
                                              )}
                                              {item.directions && (
                                                <Typography
                                                  variant='body2'
                                                  component='span'
                                                  sx={{ display: 'block', mb: 0.5 }}
                                                >
                                                  <b>Instructions:</b> {item.directions}
                                                </Typography>
                                              )}
                                              {item.prescriberName && (
                                                <Typography
                                                  variant='body2'
                                                  component='span'
                                                  sx={{ display: 'block' }}
                                                >
                                                  <b>Prescriber:</b> {item.prescriberName}
                                                </Typography>
                                              )}
                                            </Box>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                  <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                                    {lineItemsWithRx.length > 1
                                      ? 'All these items will '
                                      : 'This item will '}
                                    be transferred out.
                                  </Typography>
                                </>
                              );
                            }
                            return (
                              <Typography variant='body2' color='textSecondary'>
                                This order will be transferred out including all the items for this
                                order.
                              </Typography>
                            );
                          })()}
                        </Box>
                      )}
                      <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                        Transfer via <b>FAX</b>.
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Fulfilling Pharmacies Dropdown */}
                <Box sx={{ mt: 2 }}>
                  {(() => {
                    if (isLoadingFulfillingPharmacies) {
                      return <Alert severity='info'>Loading facilities...</Alert>;
                    }
                    if (fulfillingPharmacies.length === 0) {
                      return <Alert severity='warning'>No facilities available.</Alert>;
                    }
                    return (
                      <SelectField
                        id='TransferOut-fulfilling-pharmacy-select'
                        label='Facility'
                        name='fulfillingPharmacyId'
                        required
                        disabled={submitting || isSubmitting}
                      >
                        {fulfillingPharmacies.map((pharmacy) => (
                          <MenuItem
                            key={pharmacy.fulfillingPharmacyId}
                            value={pharmacy.fulfillingPharmacyId}
                          >
                            {pharmacy.name} - {pharmacy.city}, {pharmacy.state}
                          </MenuItem>
                        ))}
                      </SelectField>
                    );
                  })()}
                </Box>

                {/* External Pharmacies Dropdown */}
                <Box sx={{ mt: 2 }}>
                  {(() => {
                    if (isLoadingPharmacies) {
                      return <Alert severity='info'>Loading external pharmacies...</Alert>;
                    }
                    if (pharmacies.length === 0) {
                      return <Alert severity='warning'>No external pharmacies available.</Alert>;
                    }
                    return (
                      <SelectField
                        id='TransferOut-pharmacy-select'
                        label='Select External Pharmacy'
                        name='transferPharmacyId'
                        required
                        disabled={submitting || isSubmitting}
                      >
                        {pharmacies.map((pharmacy) => (
                          <MenuItem key={pharmacy.pharmacyId} value={pharmacy.pharmacyId}>
                            {pharmacy.pharmacyName} - {pharmacy.city}, {pharmacy.state}
                          </MenuItem>
                        ))}
                      </SelectField>
                    );
                  })()}
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  variant='outlined'
                  color='secondary'
                  disabled={submitting || isSubmitting}
                >
                  Cancel
                </Button>
                <LoadingButton
                  loading={submitting || isSubmitting}
                  variant='contained'
                  color='primary'
                  type='submit'
                  disabled={
                    submitting ||
                    isSubmitting ||
                    isLoadingOrderDetails ||
                    isLoadingPharmacies ||
                    isLoadingFulfillingPharmacies ||
                    orderDetails[orderId]?.isEligible === false ||
                    !values?.fulfillingPharmacyId ||
                    !values?.transferPharmacyId ||
                    fulfillingPharmacies.length === 0 ||
                    pharmacies.length === 0
                  }
                >
                  Transfer Out
                </LoadingButton>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

TransferOutDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedLineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      orderId: PropTypes.string.isRequired,
      mpi: PropTypes.string.isRequired,
      patientName: PropTypes.string.isRequired,
      medicationName: PropTypes.string.isRequired,
      quantity: PropTypes.number,
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          tagName: PropTypes.string.isRequired,
          expiry: PropTypes.string,
          createdAt: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default TransferOutDialog;
